import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import apply_and_create_hibob from "./images/apply_and_create_hibob.png";
import delete_lifecycle_status_hibob from "./images/delete_lifecycle_status_hibob.png";
import hibob_basic_info from "./images/hibob_basic_info.png";
import hibob_create_group from "./images/hibob_create_group.png";
import hibob_create_service_user from "./images/hibob_create_service_user.png";
import hibob_lifecycle from "./images/hibob_lifecycle.png";
import hibob_select_by_condition from "./images/hibob_select_by_condition.png";
import hibob_select_people_by_condition from "./images/hibob_select_people_by_condition.png";
import hibob_service_user_info from "./images/hibob_service_user_info.png";
import hibob_timeoff from "./images/hibob_timeoff.png";

const HiBob = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("hibob");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const colors = useColors();
  const permissionGroupUrl =
    "https://app.hibob.com/app-settings/permission-groups";
  const serviceUsersUrl =
    "https://app.hibob.com/app-settings/integrations/service-users";
  const loginUrl = "https://app.hibob.com/login";
  const [showSecret, setShowSecret] = useState(false);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const [formValues, setFormValues] = useState({
    service_user_id: "",
    token: "",
  });

  const {
    registerHiBobIntegration,
    getHiBobPermissions,
    getHiBobLifeCycleStatus,
    trackEvent,
  } = useAPI();

  const handleConnect = () => {
    trackEvent("User click on connect in HiBob integration");
    if (!formValues.service_user_id || !formValues.token) {
      Toast.error("Please fill in the Service User ID and Token fields");
      trackEvent(
        "User click on connect in HiBob integration with empty fields"
      );
      return;
    }

    setLoading(true);

    registerHiBobIntegration(formValues.service_user_id, formValues.token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("HiBob integration successful");
        trackEvent("User successfully connected HiBob integration");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
        trackEvent("User failed to connect HiBob integration");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputBlur = (fieldName) => {
    trackEvent("User changed HiBob integration input: + " + fieldName);
  };

  const { data: permissions } = useQuery({
    queryKey: ["hibobPermissions"],
    queryFn: getHiBobPermissions,

    select: (response) => response.data,
  });

  const { data: lifecycleStatus } = useQuery({
    queryKey: ["hibobLifecycleStatus"],
    queryFn: getHiBobLifeCycleStatus,

    select: (response) => response.data,
  });

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically fetch the relevant information from HiBob."
            : ""
        }
        image={logos.HiBobLogo}
        name={"HiBob"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>HiBob is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to HiBob">
                  <Typography variant={"body2"}>
                    Login to your HiBob account
                  </Typography>
                  <PrimaryButton
                    sx={{ width: 150 }}
                    href={loginUrl}
                    target="_blank"
                  >
                    Go to HiBob
                  </PrimaryButton>
                </GuidelineStep>
                <GuidelineStep title="Create a new API service user">
                  <Typography variant={"body2"}>
                    Click on{" "}
                    <Link
                      href={serviceUsersUrl}
                      target="_blank"
                      sx={{ color: colors.primary_main }}
                    >
                      Service Users
                    </Link>{" "}
                    to create a new API service user.
                  </Typography>
                  <Typography variant={"body2"}>
                    Enter a unique name and a display name. e.g{" "}
                    <b>Breeze-Security.</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: hibob_create_service_user,
                        altext: "HiBob create service user",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click<b> Next.</b>
                  </Typography>
                  <Typography variant={"body2"}>
                    The new API service user’s ID and token are now displayed.
                  </Typography>
                  <Typography variant={"body2"}>
                    Copy the <b>ID</b> and <b>Token</b> and paste them below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: hibob_service_user_info,
                        altext: "HiBob service user info",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />

                  {/* Text Input */}
                  <Box display="grid" mb="20px" gap="10px">
                    <TextField
                      fullWidth
                      variant="filled"
                      name="service_user_id"
                      label="Service user ID"
                      value={formValues.service_user_id}
                      onBlur={(e) => handleInputBlur("service_user_id")}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          service_user_id: e.target.value,
                        })
                      }
                      autoComplete="off"
                      disabled={guideMode}
                    />
                    <TextField
                      fullWidth
                      variant="filled"
                      type={showSecret ? "text" : "password"}
                      name="token"
                      autoComplete="off"
                      label="Token"
                      value={formValues.token}
                      onBlur={(e) => handleInputBlur("token")}
                      onChange={(e) =>
                        setFormValues({ ...formValues, token: e.target.value })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle secret visibility"
                              onClick={handleClickShowSecret}
                              onMouseDown={handleMouseDownSecret}
                            >
                              {showSecret ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      disabled={guideMode}
                    />
                  </Box>

                  <Typography variant={"body2"}>
                    Click <b>Done.</b>
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Create a permission group and add the API service user to it">
                  <Typography variant={"body2"}>
                    Click on{" "}
                    <Link
                      href={permissionGroupUrl}
                      target="_blank"
                      sx={{ color: colors.primary_main }}
                    >
                      Permission Groups
                    </Link>{" "}
                    to create a new permission group.
                  </Typography>
                  <Typography variant={"body2"}>
                    Click <b> Create Group</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: hibob_create_group,
                        altext: "HiBob create group",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Enter a name for the permission group e.g{" "}
                    <b>Breeze-Security group.</b>.
                  </Typography>
                  <Typography variant={"body2"}>
                    Under Group members, choose{" "}
                    <b>Select people by condition </b>and click{" "}
                    <b>Select People</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: hibob_select_by_condition,
                        altext: "HiBob select by condition",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    <strong>Hover your mouse over the "Select" option</strong>{" "}
                    to reveal a delete icon.
                    <br /> Then click on that icon to delete the lifecycle
                    status condition.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: delete_lifecycle_status_hibob,
                        altext: "HiBob delete lifecycle status",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    under Add specific employees, click <b>Service Users </b>
                  </Typography>
                  <Typography variant={"body2"}>
                    Select the Service User you created before and click{" "}
                    <b>Apply</b>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: apply_and_create_hibob,
                          altext: "HiBob apply and create",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Typography>
                  <Typography variant={"body2"}>
                    Click<b> Create</b>
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Set permissions to the permissions group">
                  <Typography variant={"body2"}>
                    Go to the Permission group you've created, select <br />
                    <b>
                      People's data {">"} Edit Permissions {">"} Access Rights{" "}
                    </b>
                    {">"} <b>Select people by condition.</b>
                  </Typography>
                  <Typography variant={"body2"}>
                    Select condition: Lifecycle status equals to the following:
                  </Typography>
                  <Box
                    sx={{
                      width: "700px",
                      backgroundColor: colors.grey_200,
                      padding: "16px",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color={colors.primary_darker}
                      gutterBottom
                    >
                      List of lifecycle status
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Check the box for each completed permission to track the
                      progress
                    </Typography>
                    {lifecycleStatus &&
                      lifecycleStatus.map((permission, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <Checkbox />
                          <Typography>{permission}</Typography>
                        </Box>
                      ))}
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: hibob_select_people_by_condition,
                        altext: "HiBob select people by condition",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    apply the changes and click <b>Save</b>
                  </Typography>
                  <Typography variant={"body2"}>
                    Under <strong>Access Rights</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    Give the following permissions:
                  </Typography>
                  <Box
                    sx={{
                      width: "700px",
                      backgroundColor: colors.grey_200,
                      padding: "16px",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color={colors.primary_darker}
                      gutterBottom
                    >
                      List of permissions
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Check the box for each completed permission to track the
                      progress
                    </Typography>
                    {permissions &&
                      permissions
                        .sort((a, b) => a.localeCompare(b))
                        .map((permission, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <Checkbox />
                            <Typography>{permission}</Typography>
                          </Box>
                        ))}
                  </Box>
                  <ScreenshotAccordion
                    title="View screenshot: People - Basic Info"
                    images={[
                      {
                        file: hibob_basic_info,
                        altext:
                          "HiBob Basic Info: View selected employees' Basic info sections",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <ScreenshotAccordion
                    title="View screenshot: People - Lifecycle"
                    images={[
                      {
                        file: hibob_lifecycle,
                        altext:
                          "HiBob People - Lifecycle: View selected employees' Lifecycle sections",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <ScreenshotAccordion
                    title="View screenshot: Time off"
                    images={[
                      {
                        file: hibob_timeoff,
                        altext: "HiBob time off - See who's out today",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    <Typography variant={"body2"}>Save the changes.</Typography>
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Click Connect to complete the integration">
                  <Box width={700} display="flex" justifyContent="center">
                    <PrimaryButton onClick={handleConnect} disabled={guideMode}>
                      Connect
                    </PrimaryButton>
                  </Box>{" "}
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default HiBob;
